// Video scopes drawn on a canvas: a Tektronix style diamond display on the left and an RGB parade
// waveform on the right. Both are accumulated from the same downscaled frame into one hit map.
//
// Diamond: upper x = B - G, y = B + G (black in the middle, white at the top, G left, B right);
// lower x = R - G, y = R + G downwards (white at the bottom, G left, R right).
// Pixels that stay inside the dashed diamonds are within the RGB gamut.
//
// Parade: one panel per channel (R, G, B); x is the position in the picture, y the channel value
// on a 10-bit scale (0-1023).

const SAMPLE_WIDTH = 384;
const MAX_OUTPUT_WIDTH = 960;
// Output width where text and lines have their base size
const REFERENCE_WIDTH = 640;
// Count of hits that gives full brightness (log scale); a single hit already gets MIN_LEVEL
const FULL_HITS = 24;
const MIN_LEVEL = 0.35;
// Dark enough that the traces stay readable over bright video
const BACKDROP_ALPHA = 205;
// Phosphor colors per hit map region: diamond, R, G, B panels
const COLORS = [
	[0.45, 1, 0.55],
	[1, 0.45, 0.4],
	[0.45, 1, 0.5],
	[0.6, 0.5, 1]
];
const PARADE_LEVELS = [0, 128, 256, 384, 512, 640, 768, 896, 1023];

export class DiamondScope {
	constructor (canvas) {
		this.canvas = canvas;
		this.sample = document.createElement("canvas");
		this.sample_ctx = this.sample.getContext("2d", {willReadFrequently: true});
		this.counts = null;
		this.image = null;
		this.layout = null;
		this.blocked = false;
		this.levels = new Uint8ClampedArray(FULL_HITS + 1);
		for (var i = 1; i <= FULL_HITS; i++) {
			this.levels[i] = Math.round(255 * (MIN_LEVEL + (1 - MIN_LEVEL) * Math.log1p(i - 1) / Math.log1p(FULL_HITS - 1)));
		}
	}

	// Output size follows the shown size (capped) so lines stay sharp without heavy pixel loops
	resize () {
		var w = this.canvas.clientWidth;
		var h = this.canvas.clientHeight;
		if (!w || !h) return false;
		var scale = Math.min(window.devicePixelRatio || 1, MAX_OUTPUT_WIDTH / w);
		var ow = Math.max(1, Math.round(w * scale));
		var oh = Math.max(1, Math.round(h * scale));
		if (this.canvas.width !== ow || this.canvas.height !== oh || !this.counts) {
			this.canvas.width = ow;
			this.canvas.height = oh;
			this.counts = new Uint16Array(ow * oh);
			this.image = new ImageData(ow, oh);
			this.layout = this.make_layout(ow, oh);
		}
		return true;
	}

	make_layout (w, h) {
		var k = w / REFERENCE_WIDTH;
		var margin = Math.max(4, w * 0.02);
		var top = h * 0.06;
		var bottom = h * 0.94;
		// Diamond: left third. Unit RGB values: vertical scale per unit of (a + b), horizontal per unit of (a - b)
		var dw = w * 0.34;
		var sy = (bottom - top) / 4;
		var sx = Math.min(dw * 0.4, sy * 1.6);
		var diamond = {cx: margin + dw / 2, cy: h / 2, sx: sx, sy: sy};
		// Parade: the rest, with a column for the scale labels
		var font = Math.max(8, 11 * k);
		var label_w = font * 2.6;
		var x0 = margin + dw + margin + label_w;
		var gap = Math.max(2, 4 * k);
		var panel_w = Math.max(1, (w - margin - x0 - 2 * gap) / 3);
		var panels = [0, 1, 2].map(c => x0 + c * (panel_w + gap));
		// Hit map region per output column: 0 = diamond, 1-3 = R, G, B panels
		var regions = new Uint8Array(w);
		panels.forEach((px, c) => {
			for (var x = Math.max(0, Math.floor(px)); x < Math.min(w, Math.ceil(px + panel_w)); x++) regions[x] = c + 1;
		});
		return {
			k: k,
			font: font,
			diamond: diamond,
			parade: {x0: x0, top: top, bottom: bottom, panel_w: panel_w, panels: panels},
			regions: regions
		};
	}

	draw (video) {
		if (!this.resize()) return;
		var ctx = this.canvas.getContext("2d");
		var pixels = null;
		var sw = SAMPLE_WIDTH;
		if (!this.blocked && video && video.videoWidth) {
			var sh = Math.max(1, Math.round(SAMPLE_WIDTH * video.videoHeight / video.videoWidth));
			if (this.sample.width !== sw || this.sample.height !== sh) {
				this.sample.width = sw;
				this.sample.height = sh;
			}
			try {
				this.sample_ctx.drawImage(video, 0, 0, sw, sh);
				pixels = this.sample_ctx.getImageData(0, 0, sw, sh).data;
			} catch (err) {
				// Cross-origin video without CORS: the frame can't be read
				this.blocked = true;
			}
		}
		this.accumulate(pixels, sw);
		this.render();
		ctx.putImageData(this.image, 0, 0);
		this.draw_diamond_graticule(ctx);
		this.draw_parade_graticule(ctx);
		if (this.blocked) {
			ctx.save();
			ctx.font = Math.round(this.layout.font * 1.2) + "px sans-serif";
			ctx.textAlign = "center";
			ctx.textBaseline = "middle";
			ctx.fillStyle = "rgba(220, 220, 170, 0.9)";
			ctx.fillText("Video frames not readable (CORS)", this.canvas.width / 2, this.canvas.height / 2);
			ctx.restore();
		}
	}

	accumulate (pixels, sw) {
		var w = this.canvas.width;
		var h = this.canvas.height;
		var counts = this.counts;
		counts.fill(0);
		if (!pixels) return;
		var d = this.layout.diamond;
		var p = this.layout.parade;
		var dsx = d.sx / 255;
		var dsy = d.sy / 255;
		var vy = (p.bottom - p.top) / 255;
		// Every diamond hit covers 2x2 output pixels so thin traces stay visible
		var hit2 = (x, y) => {
			if (x < 0 || y < 0 || x >= w - 1 || y >= h - 1) return;
			var o = y * w + x;
			counts[o]++;
			counts[o + 1]++;
			counts[o + w]++;
			counts[o + w + 1]++;
		};
		// Output column of every sample column in each parade panel
		var columns = p.panels.map(px => {
			var col = new Int32Array(sw);
			for (var x = 0; x < sw; x++) col[x] = Math.min(w - 1, (px + (x + 0.5) / sw * p.panel_w) | 0);
			return col;
		});
		var colR = columns[0];
		var colG = columns[1];
		var colB = columns[2];
		var bottom = p.bottom;
		// A value covers vy output rows; filling all of them avoids empty stripes between the 256 levels
		var span = Math.max(1, Math.ceil(vy));
		var last_row = Math.min(h - 1, Math.floor(bottom));
		var hit_column = (value, x) => {
			var y = (bottom - value * vy) | 0;
			var end = Math.min(last_row, y + span - 1);
			for (var o = y * w + x; y <= end; y++, o += w) counts[o]++;
		};
		for (var i = 0, n = 0; i < pixels.length; i += 4, n++) {
			var r = pixels[i];
			var g = pixels[i + 1];
			var b = pixels[i + 2];
			hit2((d.cx + (b - g) * dsx) | 0, (d.cy - (b + g) * dsy) | 0);
			hit2((d.cx + (r - g) * dsx) | 0, (d.cy + (r + g) * dsy) | 0);
			var sx = n % sw;
			hit_column(r, colR[sx]);
			hit_column(g, colG[sx]);
			hit_column(b, colB[sx]);
		}
	}

	// Hit map to phosphor colors over a dark, partly transparent backdrop
	render () {
		var w = this.canvas.width;
		var counts = this.counts;
		var data = this.image.data;
		var levels = this.levels;
		var regions = this.layout.regions;
		for (var p = 0, d = 0; p < counts.length; p++, d += 4) {
			var c = counts[p];
			if (c === 0) {
				data[d] = 0;
				data[d + 1] = 0;
				data[d + 2] = 0;
				data[d + 3] = BACKDROP_ALPHA;
			} else {
				var v = levels[c > FULL_HITS ? FULL_HITS : c];
				var color = COLORS[regions[p % w]];
				data[d] = v * color[0];
				data[d + 1] = v * color[1];
				data[d + 2] = v * color[2];
				data[d + 3] = BACKDROP_ALPHA + ((255 - BACKDROP_ALPHA) * v >> 8);
			}
		}
	}

	draw_diamond_graticule (ctx) {
		var g = this.layout.diamond;
		var k = this.layout.k;
		var diamond = (dir) => {
			ctx.beginPath();
			ctx.moveTo(g.cx, g.cy);
			ctx.lineTo(g.cx - g.sx, g.cy + dir * g.sy);
			ctx.lineTo(g.cx, g.cy + dir * 2 * g.sy);
			ctx.lineTo(g.cx + g.sx, g.cy + dir * g.sy);
			ctx.closePath();
			ctx.stroke();
		};
		ctx.save();
		ctx.strokeStyle = "rgba(200, 200, 160, 0.75)";
		ctx.lineWidth = Math.max(1, 1.2 * k);
		ctx.setLineDash([6 * k, 5 * k]);
		diamond(-1);
		diamond(1);
		ctx.setLineDash([]);
		ctx.strokeStyle = "rgba(200, 200, 160, 0.35)";
		ctx.beginPath();
		ctx.moveTo(g.cx, g.cy - 2 * g.sy);
		ctx.lineTo(g.cx, g.cy + 2 * g.sy);
		ctx.moveTo(g.cx - g.sx, g.cy - g.sy);
		ctx.lineTo(g.cx + g.sx, g.cy - g.sy);
		ctx.moveTo(g.cx - g.sx, g.cy + g.sy);
		ctx.lineTo(g.cx + g.sx, g.cy + g.sy);
		ctx.stroke();

		var font = this.layout.font;
		ctx.font = Math.round(font) + "px sans-serif";
		ctx.textBaseline = "middle";
		ctx.fillStyle = "rgba(220, 220, 170, 0.9)";
		var gap = font * 0.5;
		ctx.textAlign = "right";
		ctx.fillText("G", g.cx - g.sx - gap, g.cy - g.sy);
		ctx.fillText("G", g.cx - g.sx - gap, g.cy + g.sy);
		ctx.textAlign = "left";
		ctx.fillText("B", g.cx + g.sx + gap, g.cy - g.sy);
		ctx.fillText("R", g.cx + g.sx + gap, g.cy + g.sy);
		ctx.restore();
	}

	draw_parade_graticule (ctx) {
		var p = this.layout.parade;
		var k = this.layout.k;
		var right = p.panels[2] + p.panel_w;
		var font = this.layout.font;
		ctx.save();
		ctx.lineWidth = Math.max(1, k);
		ctx.font = Math.round(font) + "px sans-serif";
		ctx.textAlign = "right";
		ctx.textBaseline = "middle";
		PARADE_LEVELS.forEach(level => {
			var y = Math.round(p.bottom - level / 1023 * (p.bottom - p.top)) + 0.5;
			ctx.strokeStyle = "rgba(200, 180, 60, 0.55)";
			ctx.setLineDash(level === 512 ? [4 * k, 4 * k] : []);
			ctx.beginPath();
			ctx.moveTo(p.x0, y);
			ctx.lineTo(right, y);
			ctx.stroke();
			ctx.fillStyle = "rgba(220, 200, 90, 0.9)";
			ctx.fillText(String(level), p.x0 - font * 0.3, y);
		});
		ctx.setLineDash([]);
		// Panel edges
		ctx.strokeStyle = "rgba(200, 180, 60, 0.3)";
		ctx.beginPath();
		p.panels.forEach(px => {
			ctx.moveTo(Math.round(px) + 0.5, p.top);
			ctx.lineTo(Math.round(px) + 0.5, p.bottom);
			ctx.moveTo(Math.round(px + p.panel_w) - 0.5, p.top);
			ctx.lineTo(Math.round(px + p.panel_w) - 0.5, p.bottom);
		});
		ctx.stroke();
		ctx.restore();
	}
}
